import * as THREE from 'three';
import { imageFor, drawCover, fitFont } from './stageScreen';

// THE WALLS PLAY THE HOST (Khari, 2026-10-30: *"The difference would be the
// media that plays on the walls … the media of whatever artist is currently
// hosting the room."*).
//
// One canvas, drawn like the plaza's LED wall (city/stageScreen) and sharing its
// image cache and helpers: a slow crossfading slideshow of the host's own
// pictures (their covers and artwork, signed by the API, fetched through the
// asset proxy) with their NAME across the bottom. No host: the venue's name in
// big letters on a dark wash in its accent colour.
//
// Built for a DARK room: the picture is held down a stop so it reads as a glow
// in the gloom rather than a lightbox, and the room's own light (venueTypes
// `light`) stays low. Cheap: ~12 redraws a second, only while you are in the
// room (VenueScreens mounts with it), every gradient and the LED grid made once.
//
// 16:9. A screen of another shape shows the middle of it (VenueScreens crops by
// UV), so everything that must be read sits inside SAFE_W — the width a 5:4
// screen (the club's +x wall) still shows.

const W = 1024; const H = 576;
export const SCREEN_ASPECT = W / H;
const SAFE_W = Math.round(H * 1.22);
const FPS = 12;
const PER = 7;          // seconds a picture
const FADE = 1.4;       // seconds of crossfade at the end of each
const FONT = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const LABEL_FONT = `800 22px ${FONT}`;

function rgb(hex) {
  const h = String(hex || '').replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6);
  const n = parseInt(f, 16);
  return Number.isFinite(n) ? [(n >> 16) & 255, (n >> 8) & 255, n & 255] : [125, 255, 208];
}

export function makeVenueScreen() {
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  // Redrawn 12 times a second: regenerating mipmaps each time is work for a
  // screen that is never seen small enough to need them.
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;

  // The LED grid, once. Drawn over every frame with one drawImage instead of
  // four hundred fillRects.
  const grid = document.createElement('canvas');
  grid.width = W; grid.height = H;
  {
    const q = grid.getContext('2d');
    q.fillStyle = 'rgba(0,0,0,0.3)';
    for (let x = 0; x < W; x += 4) q.fillRect(x, 0, 1, H);
    for (let y = 0; y < H; y += 4) q.fillRect(0, y, W, 1);
  }
  const band = g.createLinearGradient(0, H * 0.5, 0, H);
  band.addColorStop(0, 'rgba(4,2,8,0)'); band.addColorStop(0.55, 'rgba(4,2,8,0.72)'); band.addColorStop(1, 'rgba(4,2,8,0.9)');
  const vignette = g.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, W * 0.62);
  vignette.addColorStop(0, 'rgba(0,0,0,0)'); vignette.addColorStop(1, 'rgba(0,0,0,0.6)');

  let glowFor = null; let glow = null; let sweep = null; let tint = 'rgba(125,255,208,0.9)';
  const look = (accent) => {
    if (accent === glowFor) return;
    glowFor = accent;
    const [r, gg, b] = rgb(accent);
    glow = g.createRadialGradient(W / 2, H * 0.55, 10, W / 2, H * 0.55, W * 0.55);
    glow.addColorStop(0, `rgba(${r},${gg},${b},0.38)`); glow.addColorStop(0.5, `rgba(${r},${gg},${b},0.12)`); glow.addColorStop(1, 'rgba(0,0,0,0)');
    sweep = g.createLinearGradient(0, 0, 160, 0);
    sweep.addColorStop(0, `rgba(${r},${gg},${b},0)`); sweep.addColorStop(0.5, `rgba(${r},${gg},${b},0.16)`); sweep.addColorStop(1, `rgba(${r},${gg},${b},0)`);
    tint = `rgba(${r},${gg},${b},0.92)`;
  };
  // The big line's size, worked out once per text rather than every frame.
  const fitted = new Map();
  const bigFont = (text, maxPx) => {
    const k = `${maxPx}|${text}`;
    let f = fitted.get(k);
    if (!f) {
      const px = fitFont(g, text, SAFE_W - 40, maxPx, 40);
      f = `900 ${px}px ${FONT}`;
      fitted.set(k, f);
      if (fitted.size > 24) fitted.delete(fitted.keys().next().value);
    }
    return f;
  };

  let last = 0;

  /**
   * One frame. `st` (built once per change by VenueScreens, so no strings are
   * made per frame): { title, label, hostName, media: [url], accent }.
   * Returns whether it drew (it holds to FPS).
   */
  function draw(st) {
    const now = performance.now();
    if (now - last < 1000 / FPS) return false;
    last = now;
    const t = now / 1000;
    look(st.accent);
    g.globalAlpha = 1;
    g.globalCompositeOperation = 'source-over';
    g.fillStyle = '#050307'; g.fillRect(0, 0, W, H);

    const deck = st.media;
    let shown = false;
    if (deck.length) {
      const k = Math.floor(t / PER); const into = t % PER;
      const a = imageFor(deck[k % deck.length]);
      const b = deck.length > 1 ? imageFor(deck[(k + 1) % deck.length]) : null;
      if (a) { drawCover(g, a, 0, 0, W, H, 1.04 + 0.05 * (into / PER)); shown = true; }
      if (b && into > PER - FADE) { drawCover(g, b, 0, 0, W, H, 1.04, (into - (PER - FADE)) / FADE); shown = true; }
      // Held down a stop: a glow in a dark room, not a lightbox.
      if (shown) { g.fillStyle = 'rgba(0,0,0,0.28)'; g.fillRect(0, 0, W, H); }
    }
    if (!shown) {
      // The wash: the venue's colour breathing in the middle, a slow band of
      // light crossing it.
      g.fillStyle = glow; g.fillRect(0, 0, W, H);
      const x = ((t * 60) % (W + 320)) - 160;
      g.save(); g.translate(x, 0); g.fillStyle = sweep; g.fillRect(0, 0, 160, H); g.restore();
    }
    g.fillStyle = vignette; g.fillRect(0, 0, W, H);

    g.textAlign = 'center'; g.textBaseline = 'alphabetic';
    const pulse = 0.82 + 0.18 * Math.sin(t * 1.7);
    if (st.hostName) {
      // THE HOST, across the bottom over a dark band; the venue above it, small.
      g.fillStyle = band; g.fillRect(0, H * 0.5, W, H * 0.5);
      g.font = LABEL_FONT; g.fillStyle = tint; g.globalAlpha = pulse;
      g.fillText(st.label, W / 2, H - 132);
      g.globalAlpha = 1;
      const name = st.hostName;
      g.font = bigFont(name, 96);
      g.shadowColor = tint; g.shadowBlur = 22;
      g.fillStyle = '#fff6ee';
      g.fillText(name, W / 2, H - 44);
      g.shadowBlur = 0;
    } else {
      // NOBODY HOSTING: the name of the place, big, in its own light.
      const name = st.title;
      g.font = bigFont(name, 132);
      g.shadowColor = tint; g.shadowBlur = 30;
      g.fillStyle = '#fff6ee';
      g.fillText(name, W / 2, H * 0.58);
      g.shadowBlur = 0;
      g.fillStyle = tint; g.globalAlpha = pulse;
      g.fillRect(W / 2 - 90, H * 0.58 + 26, 180, 3);
      g.globalAlpha = 1;
    }

    g.drawImage(grid, 0, 0);
    texture.needsUpdate = true;
    return true;
  }

  return { canvas, texture, draw, dispose: () => texture.dispose() };
}
